import { z } from "zod";
import { appLocale } from "../locale";
import { weightUnit } from "../weight-unit";
/**
 * No roles and no permissions: there is no RBAC in this product. Every account
 * owns its own data and nothing else, so authorization is a property of the
 * query rather than of the account. If an admin surface ever appears, a single
 * permission column is the smaller change — and a far smaller one than
 * removing an RBAC system that was built before it had a subject.
 */
export const getMeOutput = z.object({
    accountId: z.string().uuid(),
    email: z.string().email(),
    emailVerifiedAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
    /**
     * What the app calls this person (`D40`) — "Boa tarde, Marcos". `null` when
     * they never said. Display text only: never unique, never an identifier.
     *
     * ── OPTIONAL, ALTHOUGH THE SERVER ALWAYS SENDS IT ─────────────────────────
     * The deploy window runs both ways. An app built before this field parses a
     * body that has it (`z.object` strips unknown keys), and an app built after
     * it must still parse the body of an API deployed before it — or of one
     * rolled back to it. A required field here would fail every `/auth/me` in
     * that window, and a failed `/auth/me` is a sign-out.
     */
    displayName: z.string().min(1).max(60).nullable().optional(),
    /**
     * What `PUT /auth/me/locale` stored (`D93`), so a language row can read the
     * choice back. `null` is "not yet told" and is NOT English — the server
     * falls back to its default and the app to the device's. Optional for the
     * same deploy-window reason as `displayName`. The column has no `CHECK`, so
     * the server maps a stored value outside `appLocale` to `null` rather than
     * failing this response.
     *
     * `.catch(null)` is the same rule on the READING side (`D267`, `T-250`):
     * an app whose `appLocale` predates a locale another device of the same
     * account chose reads it as "not yet told" instead of failing the whole
     * response — a failed `/auth/me` is a sign-out. It protects every locale
     * added AFTER this line ships, not the five `D267` added with it.
     */
    locale: appLocale.nullable().optional().catch(null),
    /**
     * What `PUT /auth/me/time-zone` stored (`D87`) — an IANA zone name, e.g.
     * `"America/Sao_Paulo"`. `null` is "unknown", which every reader takes as
     * UTC; it is NOT a zone to guess at. Optional for the same deploy-window
     * reason as `displayName`.
     *
     * A plain bounded string and not an enum: the vocabulary is the runtime's
     * own tz database, which grows and retires names when countries change their
     * clocks, and a union here would be a second copy of it that goes stale.
     * The 64 is the same bound `setTimeZoneInput` puts on the wire and
     * `Account.changeTimeZone` puts on the column.
     */
    timeZone: z.string().min(1).max(64).nullable().optional(),
    /**
     * What `PUT /auth/me/weight-unit` stored (`D266`). `null` is "never
     * chose", which the app reads as kilograms. A display preference: every
     * load on every other route stays in kilograms. Optional for the same
     * deploy-window reason as `displayName`.
     */
    weightUnit: weightUnit.nullable().optional(),
});
/**
 * The body of `PUT /auth/me/locale` (`D93`).
 *
 * ── ONE FIELD, AND DELIBERATELY NOT AN ACCOUNT ID ───────────────────────────
 * The account is the one the verified token names. A body that could address
 * another account would let anyone with a session change somebody else's
 * language — small in itself, and the same door through which every other
 * "which account is this for?" mistake walks. Same rule, same reason, as
 * `registerDeviceInput` in `../notifications/devices.ts`.
 *
 * ── `appLocale`, THE ONE THAT ALREADY EXISTS ────────────────────────────────
 * Imported from `../locale`, never re-declared. A second copy of that union is
 * a set that gets extended in one place and not the other, and the symptom is
 * a locale the sign-in email accepts and this route rejects. `requestOtpInput`
 * reads the same export.
 *
 * ── REQUIRED, WHERE `requestOtpInput.locale` IS OPTIONAL ────────────────────
 * That one is optional because an APK older than the field reaches the sign-in
 * screen, which is the one screen with no way past it. Here there is no such
 * client: only a build that knows this route calls it at all, and a request
 * whose sole field is absent is asking for nothing.
 */
export const setLocaleInput = z.object({
    locale: appLocale,
});
/**
 * The body of `PUT /auth/me/display-name` (`D40`). Answers `204`.
 *
 * ── ONE FIELD, AND DELIBERATELY NOT AN ACCOUNT ID ───────────────────────────
 * The account is the one the verified token names — the rule
 * `setLocaleInput` states above, for the same reason. STRICT, so a body that
 * tries to add an `accountId` is refused as a whole rather than having the
 * field silently stripped and the write landing on the caller's own row
 * while the caller believes otherwise.
 *
 * ── `null` CLEARS IT ────────────────────────────────────────────────────────
 * Required and nullable, not optional: an absent field would be a request
 * asking for nothing. Trimmed before the bounds, so `"  "` is refused rather
 * than stored as a name that renders as nothing. The `Account` aggregate
 * repeats the same trim and the same 1–60 bound, keyed
 * `account.display_name_invalid`, for any caller not coming through here.
 */
export const setDisplayNameInput = z.strictObject({
    displayName: z.string().trim().min(1).max(60).nullable(),
});
/**
 * The body of `PUT /auth/me/time-zone` (`D87`). Answers `204`.
 *
 * ── ONE FIELD, AND DELIBERATELY NOT AN ACCOUNT ID ───────────────────────────
 * The account is the one the verified token names — the rule `setLocaleInput`
 * states above, for the same reason. STRICT, like `setDisplayNameInput`: a
 * body that tries to add an `accountId` is refused as a whole rather than
 * having the field silently stripped while the write lands on the caller's own
 * row and the caller believes otherwise.
 *
 * ── A BOUNDED STRING, NOT AN ENUM ───────────────────────────────────────────
 * The vocabulary is the tz database the RUNTIME ships, not a set this package
 * can hold: names are added and retired as countries change their clocks, and
 * an enum here would refuse a zone the server can handle perfectly well. So
 * the wire bounds the LENGTH — 64, where the longest real name is 32 — and
 * `Account.changeTimeZone` asks `Intl.DateTimeFormat` whether the runtime
 * knows it, keyed `account.time_zone_invalid`. Two checks, two different
 * questions, exactly as the locale route validates twice.
 *
 * ── REQUIRED, AND NOT NULLABLE ──────────────────────────────────────────────
 * Unlike `setDisplayNameInput`, there is nothing to clear: the app reads the
 * zone from the device, so it always has one to send, and a client asking to
 * go back to "unknown" is a client that has stopped knowing where it is. The
 * aggregate still accepts `null` for a caller that is not this route.
 */
export const setTimeZoneInput = z.strictObject({
    /** An IANA zone, e.g. "America/Sao_Paulo" — the app sends exactly what
     *  `Intl.DateTimeFormat().resolvedOptions().timeZone` gave it, untouched. */
    timeZone: z.string().min(1).max(64),
});
/**
 * The body of `PUT /auth/me/weight-unit` (`D266`). Answers `204`.
 *
 * STRICT and one field, for every reason `setTimeZoneInput` gives: the
 * account is the verified token's, and a body that tried to name another is
 * refused whole. Required and not nullable: the settings row always has a
 * unit to send.
 */
export const setWeightUnitInput = z.strictObject({
    weightUnit,
});
//# sourceMappingURL=me.js.map